import React, { useEffect, useRef } from 'react';

import { useInView } from 'react-intersection-observer';

import { Image, Text } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';
import { useTracking } from '@change-corgi/core-react/tracking';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { SECTION_ID_MAPPING } from '#src/app/pages/petitionDetails/shared/constants';
import { LazyComponent } from '#src/app/shared/components/layout/lazy';
import { parseSourceInfoFromUrl } from '#src/app/shared/utils/petitionVideo';

import { useSupporterVideos } from '../../../../../../hooks/supporterVoices/supporterVideos';

import { CreateVideo } from './components/CreateVideo';
import type { VideoComponentProps } from './components/VideoComponent';
import { ViewPetitionFeed } from './components/ViewPetitionFeed';

// VideoComponent is loaded in its own chunk so that @mux/mux-player-react (only reachable
// through it) stays out of the petitionDetails initial bundle and is fetched on demand when
// a card scrolls into view. The dynamic import must wrap the whole VideoComponent — not just
// PetitionVideoPlayerSunrise — because the petitionVideo barrel is not tree-shaken (no `sideEffects`
// flag), so any static import from it would drag mux back into the initial graph.
const loadVideoComponent = async (): Promise<React.ComponentType<VideoComponentProps>> =>
	(await import(/* webpackChunkName: "pages/petitionDetails-supporterVoicesVideo" */ './components/VideoComponent'))
		.VideoComponent;

const LazyVideoComponent: React.FC<VideoComponentProps & { poster: string | undefined }> = ({
	poster,
	...videoProps
}) => {
	const { ref, inView } = useInView({
		triggerOnce: true,
		rootMargin: '0px 200px',
	});

	const placeholder = (
		<div className="flex h-[320px] items-center justify-center">
			{poster ? (
				<Image src={poster} className="rounded-large aspect-auto h-full" aria-hidden />
			) : (
				<div className="rounded-large bg-surface-secondary h-full w-[180px]" />
			)}
		</div>
	);

	if (!inView) {
		return <div ref={ref}>{placeholder}</div>;
	}

	return (
		<LazyComponent
			loadComponent={loadVideoComponent}
			fallback={placeholder}
			/* oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions */
			{...(videoProps as any)}
		/>
	);
};

export const SupporterVoicesVideos: React.FC = () => {
	const {
		data: {
			petition: { id: petitionId, slug },
			supporterVoicesVideos,
		},
	} = usePetitionDetailsPageData();

	const {
		data: { currentlyPlaying, supporterVideos, showCreateVideo },
		actions: { setCurrentlyPlaying, removeVideoFromState },
	} = useSupporterVideos({ supporterVoicesVideos });

	const track = useTracking();
	const trackedPetitionIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (trackedPetitionIdRef.current === petitionId) return;
		if (supporterVideos.length === 0) return;
		trackedPetitionIdRef.current = petitionId;
		void track('petition_page_supporters_voice_view', {
			petition_id: petitionId,
			showSupporterVoices: supporterVoicesVideos.enabled,
		});
	}, [petitionId, supporterVideos.length, supporterVoicesVideos.enabled, track]);

	if (supporterVideos.length === 0) {
		return null;
	}

	return (
		<div id={SECTION_ID_MAPPING.SUPPORTER_VOICES_VIDEOS} className="flex flex-col gap-6">
			<Text as="h2" fontWeight="normal" size="x-large" variant="body">
				<Translate value="corgi.petition_gamma.videos.title" />
			</Text>

			<div className="relative max-w-full overflow-x-auto overflow-y-hidden py-6" tabIndex={0}>
				<div className="flex flex-row gap-4">
					{supporterVideos.map((video) => {
						const sourceInfo = parseSourceInfoFromUrl(video.videoUrl);
						const poster = 'poster' in sourceInfo ? sourceInfo.poster : undefined;
						return (
							<div key={video.id} className="shrink-0">
								<LazyVideoComponent
									poster={poster}
									className="h-[320px] object-cover"
									video={video}
									currentlyPlaying={currentlyPlaying}
									setCurrentlyPlaying={setCurrentlyPlaying}
									removeVideoFromState={removeVideoFromState}
								/>
							</div>
						);
					})}
					{showCreateVideo && <CreateVideo />}
				</div>
			</div>

			<ClientRender>
				<ViewPetitionFeed petitionId={petitionId} slug={slug} />
			</ClientRender>
		</div>
	);
};
